import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { AppLayout } from './layouts/AppLayout';
import { CameraRoute } from './screens/CameraRoute';
import { Documents } from './screens/Documents';
import { Home } from './screens/Home';
import { JobDetail } from './screens/JobDetail';
import { Login } from './screens/Login';
import { Notepad } from './screens/Notepad';
import { PDFViewer } from './screens/PDFViewer';
import { Photos } from './screens/Photos';
import { Completion } from './screens/Completion';
import { PostToEVA } from './screens/PostToEVA';
import { PreSyncReview } from './screens/PreSyncReview';
import { Settings } from './screens/Settings';
import { Summary } from './screens/Summary';
import { SyncProgress } from './screens/SyncProgress';

const LAST_ROUTE_KEY = 'eva-field:last-route';
const LOCAL_DEV_AUTH_BYPASS = import.meta.env.VITE_LOCAL_DEV_AUTH_BYPASS === 'true';

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);
  const locationRef = useRef(location);
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      if (LOCAL_DEV_AUTH_BYPASS) {
        if (!mounted) return;
        setSession({} as Session);
        setCheckingSession(false);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session ?? null);
      setCheckingSession(false);
    };

    void boot();

    const { data: authSubscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (LOCAL_DEV_AUTH_BYPASS) return;
      setSession(nextSession);
      if (nextSession) {
        setAuthMessage(null);
      }
    });

    const onAuthRequired = () => {
      if (LOCAL_DEV_AUTH_BYPASS) return;
      const currentLocation = locationRef.current;
      const message = 'Your session expired. Please sign in again.';
      setSession(null);
      setAuthMessage(message);
      if (currentLocation.pathname !== '/login') {
        navigate('/login', {
          replace: true,
          state: {
            from: currentLocation.pathname + currentLocation.search,
            message,
          },
        });
      }
    };

    window.addEventListener('eva-field:auth-required', onAuthRequired);

    return () => {
      mounted = false;
      authSubscription.subscription.unsubscribe();
      window.removeEventListener('eva-field:auth-required', onAuthRequired);
    };
    // navigate/location are intentionally omitted to avoid re-subscribing listeners on route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore last route after login on a cold open.
  useEffect(() => {
    if (checkingSession || !session) return;
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (location.pathname !== '/' || location.search) return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(LAST_ROUTE_KEY);
    } catch {
      saved = null;
    }
    if (saved && saved !== '/' && saved !== '/login' && saved !== '') {
      navigate(saved, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkingSession, session]);

  useEffect(() => {
    if (location.pathname === '/login') return;
    try {
      sessionStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search);
    } catch {
      // sessionStorage unavailable; silent
    }
  }, [location]);

  if (checkingSession) {
    return <div className="p-4 text-sm text-gray-500">Checking session…</div>;
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <Navigate
              to="/login"
              replace
              state={{
                from: location.pathname + location.search,
                message: authMessage ?? undefined,
              }}
            />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/job/:jobId" element={<JobDetail />} />
        <Route path="/job/:jobId/documents" element={<Documents />} />
        <Route path="/job/:jobId/photos" element={<Photos />} />
        <Route path="/job/:jobId/summary" element={<Summary />} />
        <Route path="/job/:jobId/post-to-eva" element={<PostToEVA />} />
        <Route path="/job/:jobId/wrap-up" element={<PreSyncReview />} />
      </Route>
      {/* Camera + PDF viewer + full-page notepad are full-bleed and skip AppLayout */}
      <Route path="/job/:jobId/camera" element={<CameraRoute />} />
      <Route path="/job/:jobId/documents/:docId" element={<PDFViewer />} />
      <Route path="/job/:jobId/notepad" element={<Notepad />} />
      <Route path="/job/:jobId/sync" element={<SyncProgress />} />
      <Route path="/job/:jobId/complete" element={<Completion />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
